import { asyncTaskResult, asyncTasksListing } from "../../network/network.jsx";
import { ASYNC_TASK_TYPES, VIEW_TYPES } from "../constants/OtherKeys.jsx";
import { downloadFileFromData, showToast } from "./utils.jsx";
import { EDITOR_KEYS } from "../constants/EditorKeys.jsx";
import { GRANTS } from "../constants/PrivilegeTypes.jsx";
import { IMPORT_TYPE_NAMES } from "@/other/utils/otherUtils.jsx";

export const processCompletedTask = ({
  alertItem,
  project,
  editorHandler,
  viewManager,
  boardManager,
  user,
  isBoardIndependentView,
}) => {
  asyncTasksListing().then((resp) => {
    const result = resp.data;
    let getPollingCompletedTask = {};
    if (!isBoardIndependentView) {
      getPollingCompletedTask = result.filter((pollList) => pollList.id === alertItem.asyncTask)[0];
    } else {
      getPollingCompletedTask = alertItem;
    }

    const entityType = IMPORT_TYPE_NAMES[alertItem.dataType];

    // Marketing generation tasks navigate without fetching a result — data is
    // handled by each view's own polling / context on mount.
    if (
      getPollingCompletedTask.type === ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION ||
      getPollingCompletedTask.type === ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION
    ) {
      handleMarketingGenerationComplete(viewManager);
      return;
    }

    if (getPollingCompletedTask.type === ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION) {
      asyncTaskResult(alertItem.asyncTask).then((response) => {
        handleWorkFileGenerationComplete(viewManager, response.data?.id);
      });
      return;
    }

    if (getPollingCompletedTask.type === ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION) {
      asyncTaskResult(alertItem.asyncTask).then((response) => {
        handleMarketingPageGenerationComplete(viewManager, response.data?.id);
      });
      return;
    }

    if (getPollingCompletedTask.type === ASYNC_TASK_TYPES.SITE_PUBLISH) {
      asyncTaskResult(alertItem.asyncTask).then(() => {
        handleSitePublishComplete(viewManager);
      });
      return;
    }

    asyncTaskResult(alertItem.asyncTask)
      .then((response) => {
        switch (getPollingCompletedTask.type) {
          case ASYNC_TASK_TYPES.BOARD_DUPLICATION:
            handleBoardDuplication(response, boardManager);
            break;
          case ASYNC_TASK_TYPES.CSV_IMPORT:
            handleCsvImport(response, editorHandler, user, entityType);
            break;
          case ASYNC_TASK_TYPES.CSV_EXPORT:
            handleCsvExport(response, getPollingCompletedTask.meta_data?.file_name);
            break;
          case ASYNC_TASK_TYPES.UNDO_CSV_IMPORT:
            handleUndoCsvImport(response, project);
            break;
          case ASYNC_TASK_TYPES.DEMO_BOARD_CREATION:
            handleDemoBoardCreation(response, boardManager);
            break;
          case ASYNC_TASK_TYPES.BOARD_IMPORT:
            handleBoardImport(response, boardManager);
            break;
          case ASYNC_TASK_TYPES.BOARD_EXPORT:
            handleBoardExport(response);
            break;
          case ASYNC_TASK_TYPES.BULK_ENTITY_COPY:
            handleBulkEntityCopy(response);
            break;
          default:
            console.log(`Unknown async task type: ${alertItem.type}`);
        }
      })
      .catch((error) => {
        console.error(
          `Error retrieving result for async task ${alertItem.id}:`,
          error
        );
        showToast({
          msg: `Failed to retrieve result for the completed operation.`,
          type: "error",
          duration: "medium"
        });
      });
  });
};

export const processAutoCompletedTasks = ({ completedTasksPollingList, boardManager }) => {
  /** Filter tasks that should auto-complete and haven't been processed yet */
  const autoCompleteTasks = completedTasksPollingList.filter((task) => {
    return (
      task.type === ASYNC_TASK_TYPES.CSV_EXPORT ||
      task.type === ASYNC_TASK_TYPES.BOARD_EXPORT ||
      task.type === ASYNC_TASK_TYPES.BOARD_DUPLICATION ||
      task.type === ASYNC_TASK_TYPES.DEMO_BOARD_CREATION ||
      task.type === ASYNC_TASK_TYPES.BOARD_IMPORT
    );
  });

  autoCompleteTasks.forEach((task) => {
    asyncTaskResult(task.id)
      .then((response) => {
        switch (task.type) {
          case ASYNC_TASK_TYPES.CSV_EXPORT:
            handleCsvExport(response, task.meta_data?.file_name);
            break;
          case ASYNC_TASK_TYPES.BOARD_EXPORT:
            handleBoardExport(response);
            break;
          case ASYNC_TASK_TYPES.BOARD_DUPLICATION:
          case ASYNC_TASK_TYPES.BOARD_IMPORT:
          case ASYNC_TASK_TYPES.DEMO_BOARD_CREATION:
            handleBoardListingUpdate(response, boardManager);
            break;
          default:
            console.log(`Unknown auto-complete task type: ${task.type}`);
        }
      })
      .catch((error) => {
        console.error(`Error auto-completing task ${task.id}:`, error);
        showToast({
          msg: `Failed to auto-complete operation of async tasks.`,
          type: "error",
          duration: "medium"
        });
      });
  });
};

const handleBoardListingUpdate = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    boardManager.getRefreshUserBoardsListing();
  } else {
    showToast({
      msg: result.message || "Failed to update board listings.",
      type: "error",
      duration: "medium"
    });
  }
}

const handleCsvImport = (response, editorHandler, user, entityType) => {
  const result = response.data;
  const { created_count = 0, updated_count = 0, error_count = 0 } = result;
  const totalImportedRecords = created_count + updated_count + error_count;
  /** totalImportedRecords === 0 - Its means no records were imported, an uploaded file is empty */
  if (totalImportedRecords === 0) {
    showToast({
      msg: "No records were imported. The file may be empty or contain invalid data.",
      type: "warning"
    });
  } else {
    editorHandler.openEditor(EDITOR_KEYS.importEditor, null, {
      importType: entityType.toLowerCase(), canEdit: user && user.can(GRANTS.grant_one), importHeader: entityType, asyncTaskImportResult: result }, { useExisting: true, by: "recordType" }
    )
  }
}


/**
 * Handle CSV export result
 * This method processes the result of a CSV export task.
 * It converts the CSV data from the backend into a Blob object for download.
 * @param response
 * @param fileName
 */
const handleCsvExport = (response, fileName) => {
  const result = response.data;
  if (result) {
    const blob = new Blob([result], { type: 'text/csv;charset=utf-8;' });
    downloadFileFromData(blob, fileName);
    showToast({
      msg: `Successfully exported ${fileName || 'data'} to CSV.`,
      type: "success",
      duration: "medium"
    });
  } else {
    showToast({
      msg: result?.message || "Failed to export to CSV.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle undo CSV import result
 * This method processes the result of an undo CSV import task.
 * The backend will broadcast the changes via WebSocket, so we don't need to reload the project.
 * It also calls the handleToggleContextMenu method to update the history tab content menu of current row.
 * @param response
 * @param project
 */
const handleUndoCsvImport = (response, project) => {
  const result = response.data;
  if (response.status === 200) {
    /** This is here because on async task implementation,
     when the undo is completed, we need to update the context menu and toolbar button states if user do not close the editor,
     so we assign that event here and listen in HistoryTab
     */
    project.importBatchStore.trigger("undoCompletes");
  } else {
    showToast({
      msg: result.message || "Failed to undo CSV import.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle demo board creation result
 * This method processes the result of a demo board creation task and shows a success or error notification.
 * It also refreshes the board list if the operation was successful.
 * @param response
 * @param boardManager
 */
const handleDemoBoardCreation = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    const currentSelectionBoard = boardManager.boardNameStore.query((board) => board.id === result.id, true)[0];
    boardManager.changeSelectedBoard(currentSelectionBoard);
  } else {
    showToast({
      msg: result.message || "Failed to create demo board.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle a board import result
 * This method processes the result of a board import task.
 * It also switches board if the operation was successful.
 * @param response
 * @param boardManager
 */
const handleBoardImport = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    if (typeof result === "object") {
      const currentSelectionBoard = boardManager.boardNameStore.query((board) => board.id === result.id, true)[0]
      boardManager.changeSelectedBoard(currentSelectionBoard)
    }
  } else {
    showToast({
      msg: result.message || "Failed to import board.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle a board export result
 * This method processes the result of a board export task.
 * Download the file also
 * @param response
 */
const handleBoardExport = (response) => {
  const result = response.data;
  if (response.status === 200) {
    // convert blob to json
    const resultJson = new Blob([JSON.stringify(result)], { type: "application/json" });
    downloadFileFromData(resultJson, result.Board[0].display_name, "json");
  } else {
    showToast({
      msg: result.message || "Failed to export board.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle a bulk entity copy result.
 * Shows a success toast if all records were copied, or reports how many
 * could not be copied due to duplicates on the destination board.
 * @param response
 */
const handleBulkEntityCopy = (response) => {
  if (response.status !== 200) {
    showToast({
      msg: "Failed to retrieve bulk copy results.",
      type: "error",
      duration: "medium"
    });
    return;
  }

  const results = response.data;
  if (!Array.isArray(results) || results.length === 0) {
    showToast({ msg: "All records were copied successfully.", type: "success" });
    return;
  }

  const notCopiedCount = results.filter((r) => r.duplicate_exists).length;
  const copiedCount = results.length - notCopiedCount;

  if (notCopiedCount === 0) {
    showToast({ msg: `All ${results.length} records copied successfully.`, type: "success" });
  } else if (copiedCount === 0) {
    showToast({
      msg: `No records copied — all ${results.length} already exist on the destination board.`,
      type: "warning",
      duration: "medium"
    });
  } else {
    showToast({
      msg: `${copiedCount} copied, ${notCopiedCount} skipped — already exist on the destination board.`,
      type: "warning",
      duration: "medium"
    });
  }
};

const navigateToViewByType = (viewManager, viewType, viewName, otherParams = {}) => {
  if (!viewManager) return;
  const view = viewManager.getDefaultViewByType(viewType);
  const hasOtherParams = Object.keys(otherParams).length > 0;
  if (view) {
    viewManager.selectView(view, { otherParams: hasOtherParams ? otherParams : undefined });
  } else {
    viewManager.openAnonymousViewByType({ viewType, viewName, otherParams });
  }
};

/**
 * Handle a marketing manifest or tags generation completion triggered by banner double-click.
 * Navigates to Settings → SMM Generator. The SMMGeneratorContext's own polling
 * will detect the completed task and populate the editor or open the tag review dialog.
 * @param viewManager
 */
const handleMarketingGenerationComplete = (viewManager) => {
  navigateToViewByType(viewManager, VIEW_TYPES.settings, "Settings", {
    setting: "set-smm-generator",
  });
};

/**
 * Handle a marketing work file generation completion triggered by banner double-click.
 * @param viewManager
 * @param workFileId
 */
const handleWorkFileGenerationComplete = (viewManager, workFileId) => {
  navigateToViewByType(
    viewManager,
    VIEW_TYPES.workFiles,
    "Work Files",
    workFileId ? { workFileId } : {},
  );
};

const handleMarketingPageGenerationComplete = (viewManager, marketingPageId) => {
  navigateToViewByType(
    viewManager,
    VIEW_TYPES.marketingPages,
    "Pages",
    marketingPageId ? { marketingPageId } : {},
  );
};

const handleSitePublishComplete = (viewManager) => {
  navigateToViewByType(viewManager, VIEW_TYPES.marketingPages, "Pages", {});
};

/**
 * Handle a board duplication result
 * It also switching board if the operation was successful.
 * @param response
 * @param boardManager
 */
const handleBoardDuplication = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    const currentSelectionBoard = boardManager.boardNameStore.query((board) => board.id === result.id, true)[0];
    boardManager.changeSelectedBoard(currentSelectionBoard);
  } else {
    showToast({
      msg: result.message || "Failed to duplicate board.",
      type: "error",
      duration: "medium"
    });
  }
}
